<template>
  <div class="wrapper" :class="{'sidebar-shown': displaySidebar, 'sidebar-open': sidebarOpen}">
    <sidebar active="5" @openSidebar="sidebarOpen = true" @closeSidebar="sidebarOpen = false" @saveProgress="showSaveProgress = true"/>
    <main id="main" class="content has-sidebar">
      <app-header :back="back"></app-header>
      <header class="page summary-view">
        <h1 class="title">Mix Summary</h1>
        <div class="mix-totals">
          <div>
            <del v-if="discount">{{ finalCostPerAcre | toUSD }}</del>
            <span v-if="discount">{{ discountedCostPerAcre | toUSD }}</span>
            <span v-if="!discount">{{ finalCostPerAcre | toUSD }}</span>
            <small>/ Acre</small>
            <p v-if="discount">You received a {{ discount }}% discount on your order <br/>based on your acreage.</p>
          </div>
          <div>
            {{ grandTotal | toUSD }}
            <small>Total</small>
          </div>
        </div>
      </header>
      <div v-if="!haveRequiredFields">
        <div class="alert">
          We don't have enough information to complete this mix. Please use the back button or <router-link to="/start">start over</router-link> and fill in all the required information.
        </div>
      </div>
      <div v-else>
        <div class="summary-panels row">
          <div class="col-lg-7">
            <summary-table
            :sum="cropRatesSum"
            :target="mixTarget"
            :canEdit="canEdit"></summary-table>
            <div class="mix-scorecard">
              <h2>Total Mix Scorecard</h2>
              <div class="items">
                <div v-for="(value, index) in stats" :key="index">
                  <stat-bar :value="value" :facetIndex="index"></stat-bar>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-5">
            <div class="order-details">
              <h2>Order Details</h2>
              <div class="items">
                <dl>
                  <dt>Acres</dt>
                  <dd>{{ formData.acres }}</dd>
                </dl>
                <dl>
                  <dt>Method</dt>
                  <dd>{{ this.$store.state.plantingMethods[formData.plantingMethod] }}</dd>
                </dl>
                <dl>
                  <dt>Packaging</dt>
                  <dd>{{ formData.packagingOption }}</dd>
                </dl>
                <dl>
                  <dt>Season</dt>
                  <dd>{{ plantingTime }}</dd>
                </dl>
                <dl>
                  <dt>Inoculant</dt>
                  <dd>{{ inoculantOption }}</dd>
                </dl>
                <dl v-if="minimumPlantingDepth">
                  <dt>Min. Depth</dt>
                  <dd>{{ minimumPlantingDepth }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div class="button-row">
          <a target="_blank" :href="checkoutUrl" class="btn btn-primary has-arrow">Proceed To Checkout</a>
        </div>
      </div>
    </main>
    <save-progress :shown="showSaveProgress" @close="showSaveProgress = false" />
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import SummaryTable from '@/components/SummaryTable'
import Header from '@/components/Header'
import StatBar from '@/components/StatBar'
import SaveProgress from '@/components/SaveProgress'
export default {
  components: {
    'sidebar': Sidebar,
    'app-header': Header,
    'stat-bar': StatBar,
    'summary-table': SummaryTable,
    'save-progress': SaveProgress
  },
  data () {
    return {
      back: 'inoculation',
      displaySidebar: true,
      sidebarOpen: false,
      showSaveProgress: false,
      canEdit: false,
      plantingTimes: [
        'Early - Mid Spring',
        'Late Spring - Early Summer',
        'Mid-Summer',
        'Late Summer - Early Fall',
        'Mid / Late Fall'
      ],
      inoculantOptions: [
        'None',
        'Separate'
      ]
    }
  },
  methods: {
    handleCropRemoved (crop) {
      this.$emit('cropRemoved', crop) // bubble up to App.vue
    },
    getFormattedValue (value) {
      return Math.round(value * 100) / 100
    },
    getMinDepth () {
      let minDepth = 100
      if (this.formData.plantingMethod !== 0) {
        minDepth = '0'
      } else {
        this.formData.selectedCrops.forEach((entry, index) => {
          let seedingDepth = entry.crop.seedingDepth.split('-')
          if (seedingDepth[0] < minDepth) {
            minDepth = seedingDepth[0].trim()
          }
        })
      }
      return minDepth
    },
    handleEdit () {
      this.$router.push({ name: 'species' })
    },
    handleRemove (crop) {
      this.$emit('cropRemoved', crop) // bubble up to App.vue
    }
  },
  computed: {
    formData () {
      return this.$store.state.formData
    },
    totals () {
      return this.$store.state.totals
    },
    plantingTime () {
      let timeIndex = this.formData.plantingTime - 1 // seasons have a 1-5 value attached for recommendation matching
      return this.plantingTimes[timeIndex]
    },
    inoculantOption () {
      return this.inoculantOptions[this.formData.inoculantOption]
    },
    discount () {
      return this.totals.discount
    },
    finalCostPerAcre () {
      return this.getFormattedValue(this.totals.seedCostPerAcre + this.totals.inoculantCostPerAcre + this.totals.blendingFeePerAcre)
    },
    discountedCostPerAcre () {
      return this.getFormattedValue(this.finalCostPerAcre - (this.finalCostPerAcre * this.totals.discount / 100))
    },
    grandTotal () {
      return this.getFormattedValue(this.discountedCostPerAcre * this.formData.acres)
    },
    mixTarget () {
      return this.$store.state.mixTargets[this.$store.state.formData.plantingMethod]
    },
    cropRatesSum () {
      let sum = 0
      this.formData.selectedCrops.forEach((entry, index) => {
        sum += entry.amount / entry.crop.seedingRate * 100
      })
      return Math.round(sum * 100) / 100
    },
    minimumPlantingDepth () {
      let depth = null
      if (this.formData.plantingMethod === 0) {
        depth = this.getMinDepth() + '"'
      }
      return depth
    },
    haveRequiredFields () {
      let result = true
      if (!this.formData.acres ||
      !this.formData.mixName ||
      !this.formData.packagingOption ||
      !this.formData.selectedCrops.length) {
        result = false
      }
      return result
    },
    checkoutUrl () {
      let crops = []
      this.formData.selectedCrops.forEach((entry, index) => {
        let item = [
          entry.crop['id'].replace('?', ''),
          entry.crop['species'],
          entry.amount,
          this.getFormattedValue(entry.amount / entry.crop['seedingRate'])
        ]
        crops.push(item.join('|'))
      })
      let score = []
      this.$store.state.cropFacets.forEach((facet, index) => {
        score.push(facet.name + '|' + facet.labels[this.stats[index]])
      })
      let params = [
        'name=' + this.formData.mixName,
        'acres=' + this.formData.acres,
        'ppa=' + this.totals.lbsPerAcre,
        'cpa=' + this.totals.seedCostPerAcre,
        'bfpa=' + this.totals.blendingFeePerAcre,
        'ipa=' + this.totals.inoculantCostPerAcre,
        'discount=' + this.getFormattedValue(parseFloat(this.finalCostPerAcre) - parseFloat(this.discountedCostPerAcre)),
        'total=' + this.grandTotal,
        'crops=' + crops.join(','),
        'inoculant=' + this.inoculantOption,
        'packaging=' + this.formData.packagingOption,
        'depth=' + this.getMinDepth(),
        'score=' + score.join(',')
      ]
      return encodeURI(this.$store.state.storeDomain + '/?mix&' + params.join('&'))
    },
    stats () {
      let nitroRating = 0
      let grazRating = 0
      let compRating = 0
      let salRating = 0
      let cnRating = 0
      let scavRating = 0
      this.formData.selectedCrops.forEach((entry, index) => {
        let percentOfMix = entry.amount / this.totals.lbsPerAcre // weight the scores
        nitroRating += entry.crop.nitrogenFixationRating * percentOfMix
        grazRating += entry.crop.grazingRating * percentOfMix
        compRating += entry.crop.compactionRating * percentOfMix
        salRating += entry.crop.salinityToleranceRating * percentOfMix
        cnRating += entry.crop.cnRatio * percentOfMix
        scavRating += entry.crop.scavengingRating * percentOfMix
      })
      return [
        Math.ceil(nitroRating),
        Math.ceil(grazRating),
        Math.ceil(compRating),
        Math.ceil(salRating),
        Math.ceil(cnRating),
        Math.ceil(scavRating)
      ]
    }
  }
}
</script>

<style lang="scss">
  header.summary-view {
    margin: 0 0 30px;
    padding-bottom: 20px;
    text-align: left;
    border-bottom: 1px solid rgba($color-white, 0.5);
    @include media-breakpoint-up(lg){
      margin-top: 0;
      display: flex;
      justify-content: space-between;
    }
  }
  .mix-totals {
    font-size: 30px;
    font-weight: normal;
    @include media-breakpoint-up(md){
      display: flex;
    }
    div{
      @include media-breakpoint-up(md){
        width: 50%;
      }
      @include media-breakpoint-up(lg){
        width: auto;
        margin-left: 60px;
      }
    }
    small{
      font-size: 46%;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding-left: 4px;
    }
    del{
      opacity: .5;
      margin-right: 20px;
      text-decoration: none;
      position: relative;
      &::after{
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        height: 1px;
        background: $color-white;
      }
    }
    p{
      font-size: 13px
    }
  }
  .button-row {
    text-align: left;
    .btn{
      margin: 0;
      @include media-breakpoint-up(lg){
        width: auto;
        display: inline-block;
      }
    }
  }
  .summary-panels{
    .summary-table,
    .order-details,
    .mix-scorecard{
      padding: 20px 15px 0 15px;
      background: $color-dark-white;
      color: $color-dark-green;
      border-radius: 3px;
      text-align: left;
      @include media-breakpoint-up(md){
        padding-left: 30px;
        padding-right: 30px;
      }
      h2{
        font-weight: 600;
        margin-bottom: 15px;
      }
      .items{
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
      }
    }
    .summary-table,
    .mix-scorecard{
      margin-bottom: 15px;
      @include media-breakpoint-up(lg){
        margin-bottom: 30px;
      }
    }
    .summary-table{
      padding-left: 0;
      padding-right: 0;
    }
    .order-details{
      margin-bottom: 15px;
      dl{
        width: calc(50% - 15px);
        margin-bottom: 20px;
        dt{
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          opacity: .7;
          letter-spacing: 1px;
        }
        dd{
          font-weight: 600;
          margin: 0;
        }
      }
    }
    .mix-scorecard{
      .items > div{
        width: 100%;
        @include media-breakpoint-up(lg){
          width: calc(50% - 15px);
        }
      }
      .stat{
        margin-top: 0;
        margin-bottom: 40px;
      }
    }
  }
  .crop-groups.summary-view{
    padding-top: 15px;
    margin-bottom: 40px;
    @include media-breakpoint-up(lg){
      padding-top: 30px;
    }
    .crop-group{
      padding: 0;
    }
    .crop-category .title{
      pointer-events: none;
      &::after{
        display: none;
      }
    }
    .crop-buttons .amount{
      background: none;
      pointer-events: none;
      padding-left: 0;
    }
  }
</style>
